/**
 * The writes of this session that a list does not show yet. A write is pending from `start`.
 * The first load that started after the answer shows the write, so that load ends it. A failed
 * write stays until `start` replaces it or `drop` removes it.
 */

import { atom, type Getter, type Setter, type WritableAtom } from 'jotai';

import { type Write, type Writes, type WritesAtoms, WriteState } from '@/types/writes';

export function writesAtoms<R>(reload: WritableAtom<null, [], void>): WritesAtoms<R> {
    const writes = atom<Writes<R>>(new Map());

    const drop = atom(null, (get, set, id: string): void => {
        const remainingWrites = new Map(get(writes));
        if (remainingWrites.delete(id)) {
            set(writes, remainingWrites);
        }
    });

    const start = atom(null, (get, set, id: string, request: R): void => {
        const write: Write<R> = { ...request, state: WriteState.Saving };
        set(writes, new Map(get(writes)).set(id, write));
    });

    const answer = atom(null, (get, set, id: string): void => {
        const write = get(writes).get(id);
        if (write !== undefined) {
            set(writes, new Map(get(writes)).set(id, {
                ...write,
                state: WriteState.Saved,
                answeredAtMs: Date.now(),
            }));
        }
        set(reload);
    });

    const fail = atom(null, (get, set, id: string, error: Error): void => {
        const write = get(writes).get(id);
        if (write !== undefined) {
            set(writes, new Map(get(writes)).set(id, {
                ...write,
                state: WriteState.Failed,
                error,
            }));
        }
    });

    function endAnswered(get: Getter, set: Setter, startedAtMs: number): void {
        const currentWrites = get(writes);
        const remainingWrites = new Map<string, Write<R>>();
        for (const [id, write] of currentWrites) {
            const isEnded = write.answeredAtMs !== undefined && startedAtMs >= write.answeredAtMs;
            if (!isEnded) {
                remainingWrites.set(id, write);
            }
        }
        if (remainingWrites.size !== currentWrites.size) {
            set(writes, remainingWrites);
        }
    }

    return { writes, start, answer, fail, drop, endAnswered };
}
